import React from 'react';
import { Card, Table, Button, Badge } from 'react-bootstrap';
import { formatDate, formatAddress } from '../../utils/formatters';

/**
 * Displays usage history for a single service agreement.
 */
const ServiceAgreementPanel = ({ serviceAgreement, accountId, onExport }) => {
  const premise = serviceAgreement.premise;
  const usage = serviceAgreement.usage || [];

  const getPrimaryHeatingSource = () => {
    const typeDesc = serviceAgreement.typeDescription;
    if (!typeDesc) return 'N/A';

    const spaceIndex = typeDesc.indexOf(' ');
    const hyphenIndex = typeDesc.indexOf('-');
    let index = -1;

    if (spaceIndex !== -1) index = spaceIndex;
    else if (hyphenIndex !== -1) index = hyphenIndex;

    if (index === -1) {
      return typeDesc.charAt(0).toUpperCase() + typeDesc.slice(1).toLowerCase();
    }

    const source = typeDesc.substring(0, index).toLowerCase();
    return source.charAt(0).toUpperCase() + source.slice(1);
  };

  const getPaymentSource = (record) => {
    if (record.description?.toLowerCase() === 'billing charge') {
      return '---';
    }
    return record.paymentFromEnergyAssistance ? record.fundType : 'Customer';
  };

  const handleExport = () => {
    if (!usage || usage.length === 0) return;

    const rows = [];

    // Header row with account ID
    rows.push(['Account ID', accountId]);

    // Column headers
    rows.push([
      'Bill Date',
      'Transaction',
      'Days',
      `${serviceAgreement.usageUnit || ''} Usage`,
      'Rate Discount',
      'Billed Amount',
      'Account Balance',
      'Payment Source',
    ]);

    // Data rows
    usage.forEach((record) => {
      rows.push([
        formatDate(record.arrearsDate),
        record.description || '',
        record.days || '',
        record.unitQty || '',
        record.formattedRateDiscount || '',
        record.formattedAmount || '',
        record.formattedCurrentBalance || '',
        getPaymentSource(record),
      ]);
    });

    // Convert to CSV string
    const csvContent = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `usage_history_${serviceAgreement.id || 'export'}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <Card className="shadow mb-4">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h6 className="mb-1">Service Agreement [{serviceAgreement.id}] &nbsp;
                  <Badge bg={serviceAgreement.status === 'Active' ? 'success' : 'secondary'}>
                    {serviceAgreement.status}
                  </Badge></h6>
            {premise && (
              <div className="small">
                {formatAddress(premise)}
              </div>
            )}
            <div className="small">
              Premise Type: {premise?.formattedType || serviceAgreement.typeDescription || 'N/A'}
            </div>
            <div className="small">
              Primary Heating Source: {getPrimaryHeatingSource()}
            </div>
          </div>
          {usage.length > 0 && (
            <Button variant="primary" size="sm" onClick={onExport || handleExport}>
              Export CSV
            </Button>
          )}
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {usage.length === 0 ? (
          <div className="p-3 text-muted">No usage records found.</div>
        ) : (
          <div className="table-responsive">
            <Table striped hover className="mb-0">
              <thead>
                <tr>
                  <th>Bill Date</th>
                  <th>Transaction</th>
                  <th>Days</th>
                  <th>{serviceAgreement.usageUnit || ''} Usage</th>
                  <th>Rate Discount</th>
                  <th>Billed Amount</th>
                  <th>Account Balance</th>
                  <th>Payment Source</th>
                </tr>
              </thead>
              <tbody>
                {usage.map((record, idx) => (
                  <tr key={idx}>
                    <td>{formatDate(record.arrearsDate)}</td>
                    <td>
                      {record.description}
                      {record.paymentFromEnergyAssistance && (
                        <span
                          className="ms-1 text-primary"
                          title="Payment from Energy Assistance"
                          style={{ cursor: 'help' }}
                        >
                          **
                        </span>
                      )}
                    </td>
                    <td>{record.days || ''}</td>
                    <td>{record.unitQty && record.unitQty !== 0 ? record.unitQty : ''}</td>
                    <td>{record.formattedRateDiscount || ''}</td>
                    <td>{record.formattedAmount || ''}</td>
                    <td>{record.formattedCurrentBalance || ''}</td>
                    <td>{getPaymentSource(record)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ServiceAgreementPanel;